import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Faltan las variables de entorno de Supabase');
}

// Configuración con reintentos y timeouts más largos
export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
  },
  global: {
    fetch: (...args) => {
      return fetch(...args).catch(err => {
        console.error('Error de red en Supabase:', err);
        // Proporcionar un mensaje de error más amigable
        throw new Error('No se pudo conectar a la base de datos. Por favor, verifica tu conexión a internet e intenta de nuevo.');
      });
    },
  },
});

// Tipos para la base de datos
export type CV = {
  id: string;
  user_id: string;
  original_cv: string;
  optimized_cv: string;
  job_url: string;
  created_at: string;
}

export type UserCoins = {
  id: string;
  user_id: string;
  balance: number;
  coins?: number;  // Opcional para compatibilidad con registros antiguos
  created_at: string;
  updated_at: string;
}

export type CoinTransaction = {
  id: string;
  user_id: string;
  amount: number;
  description: string;
  created_at: string;
  type: string; // Tipo de transacción: 'purchase', 'usage', etc.
}

export type CoinPackage = {
  id: string;
  name: string;
  coins: number;
  price: number;
  currency: string;
  discount?: number; // Porcentaje de descuento opcional
  cvs: number;
  bestValue?: boolean;
}

// Paquetes de monedas disponibles - Ahora soporta USD y CLP
// NOTA: Estos paquetes se usan como fallback. El hook useCurrencyPackages() debería usarse preferentemente
export const coinPackages: CoinPackage[] = [
  // Paquetes CLP (Chile)
  {
    id: 'basic',
    name: 'Starter',
    coins: 1,
    price: 3990,
    currency: 'CLP',
    discount: 0,
    cvs: 1
  },
  {
    id: 'standard',
    name: 'Paquete Estándar',
    coins: 3,
    price: 7490,
    currency: 'CLP',
    discount: 0,
    cvs: 3
  },
  {
    id: 'premium',
    name: 'Advanced',
    coins: 10,
    price: 9990,
    currency: 'CLP',
    discount: 0,
    cvs: 10,
    bestValue: true
  },
  // Paquetes USD (Internacional)
  {
    id: 'basic-usd',
    name: 'Starter',
    coins: 1,
    price: 4.99,
    currency: 'USD',
    discount: 0,
    cvs: 1
  },
  {
    id: 'standard-usd',
    name: 'Standard Package',
    coins: 3,
    price: 8.99,
    currency: 'USD',
    discount: 0,
    cvs: 3
  },
  {
    id: 'premium-usd',
    name: 'Advanced',
    coins: 10,
    price: 11.99,
    currency: 'USD',
    discount: 0,
    cvs: 10,
    bestValue: true
  }
];

// Función de utilidad para reintentar operaciones
const withRetry = async (operation: () => Promise<any>, maxRetries = 3, delay = 1000) => {
  let lastError;
  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      return await operation();
    } catch (error) {
      console.warn(`Intento ${attempt + 1}/${maxRetries} falló:`, error);
      lastError = error;
      if (attempt < maxRetries - 1) {
        await new Promise(resolve => setTimeout(resolve, delay * (attempt + 1)));
      }
    }
  }
  throw lastError;
};

// Funciones para interactuar con la base de datos
export const saveCV = async (userId: string, originalCV: string, optimizedCV: string, jobUrl: string) => {
  return withRetry(async () => {
    const { data, error } = await supabase
      .from('cvs')
      .insert([
        {
          user_id: userId,
          original_cv: originalCV,
          optimized_cv: optimizedCV,
          job_url: jobUrl
        }
      ])
      .select();

    if (error) throw error;
    return data;
  });
};

export const getUserCVs = async (userId: string) => {
  return withRetry(async () => {
    const { data, error } = await supabase
      .from('cvs')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data as CV[];
  });
};

// Funciones para el sistema de coins
export const getUserCoins = async (userId: string): Promise<UserCoins | null> => {
  try {
    return await withRetry(async () => {
      const { data, error } = await supabase
        .from('user_coins')
        .select('*')
        .eq('user_id', userId)
        .single();

      if (error) {
        console.error('Error al obtener coins del usuario:', error);
        return null;
      }

      // Verificar qué columna usar para el balance
      if (data) {
        // Si existe la columna balance, usarla; de lo contrario, usar coins
        if ('balance' in data && data.balance !== null) {
          data.balance = data.balance;
        } else if ('coins' in data && data.coins !== null) {
          data.balance = data.coins;
        } else {
          data.balance = 0;
        }
      }

      return data as UserCoins;
    });
  } catch (error) {
    console.error('Error al obtener coins del usuario después de reintentos:', error);
    // Proporcionar un valor predeterminado para evitar errores en la UI
    return {
      id: 'temp',
      user_id: userId,
      balance: 0,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    };
  }
};

export const createUserCoinsIfNotExists = async (userId: string): Promise<UserCoins | null> => {
  try {
    // Verificar si ya existe
    const existingCoins = await getUserCoins(userId);
    if (existingCoins && existingCoins.id !== 'temp') return existingCoins;

    // Crear nuevo registro
    return await withRetry(async () => {
      const { data, error } = await supabase
        .from('user_coins')
        .insert([{
          user_id: userId,
          balance: 0,  // Sin bonus inicial - los usuarios deben comprar
          coins: 0     // Inicializar ambas columnas
        }])
        .select()
        .single();

      if (error) {
        console.error('Error al crear coins para el usuario:', error);
        return null;
      }

      return data as UserCoins;
    });
  } catch (error) {
    console.error('Error al crear coins para el usuario después de reintentos:', error);
    return null;
  }
};

// Registrar transacción en todas las tablas relevantes
export const registerTransactionInAllTables = async (
  userId: string,
  amount: number,
  type: string = 'purchase',
  description: string = 'Transacción'
): Promise<boolean> => {
  console.log(`[TRANS-DEBUG] Registrando transacción en todas las tablas: ${userId}, ${amount}, ${description}`);

  try {
    // Crear un identificador único para esta transacción para evitar duplicados
    const uniqueId = `${userId}_${amount}_${type}_${Date.now()}`;

    // Verificar si ya se está registrando una transacción similar (protección contra duplicados)
    if (typeof window !== 'undefined') {
      const transactionKey = `registering_transaction_${uniqueId}`;
      if (window.sessionStorage.getItem(transactionKey)) {
        console.log(`[TRANS-DEBUG] Transacción similar ya en proceso, evitando duplicación`);
        return false;
      }

      // Marcar como en proceso
      window.sessionStorage.setItem(transactionKey, 'true');

      // Eliminar la marca después de 5 segundos (por si acaso)
      setTimeout(() => {
        window.sessionStorage.removeItem(transactionKey);
      }, 5000);
    }

    // Registrar en la tabla principal coin_transactions
    const { data, error: txError } = await supabase
      .from('coin_transactions')
      .insert([
        {
          user_id: userId,
          amount: amount,
          type: type,
          description: description,
          status: 'completed'
        }
      ])
      .select();

    if (txError) {
      console.error('[TRANS-DEBUG] Error al registrar en coin_transactions:', txError);
      return false;
    } else {
      console.log('[TRANS-DEBUG] Transacción registrada correctamente en coin_transactions');

      // Disparar evento de actualización de monedas
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('coins-updated'));
      }

      return true;
    }
  } catch (error) {
    console.error('[TRANS-DEBUG] Error al registrar transacción:', error);
    return false;
  }
};

export const updateUserCoins = async (userId: string, amount: number, description: string): Promise<boolean> => {
  try {
    console.log(`[COINS-DEBUG] Iniciando actualización de coins para usuario ${userId}. Monto: ${amount}, Descripción: ${description}`);

    return await withRetry(async () => {
      // Iniciar transacción
      const { data: userData, error: userError } = await supabase
        .from('user_coins')
        .select('*')  // Seleccionar todos los campos en lugar de solo 'balance'
        .eq('user_id', userId)
        .single();

      if (userError) {
        console.error('[COINS-DEBUG] Error al obtener coins del usuario:', userError);

        // Si el usuario no existe, intentar crearlo
        if (userError.code === 'PGRST116') {
          console.log('[COINS-DEBUG] Usuario no encontrado, creando nuevo registro de coins');
          const { error: createError } = await supabase
            .from('user_coins')
            .insert([{
              user_id: userId,
              balance: amount,
              coins: amount  // Actualizar ambas columnas
            }]);

          if (createError) {
            console.error('[COINS-DEBUG] Error al crear registro de coins para el usuario:', createError);
            return false;
          }

          // Registrar transacción en todas las tablas
          await registerTransactionInAllTables(userId, amount, 'credit', description);

          return true;
        }

        return false;
      }

      // Verificar qué columna usar para el balance actual
      let currentCoins = 0;
      if ('balance' in userData && userData.balance !== null) {
        currentCoins = userData.balance;
      } else if ('coins' in userData && userData.coins !== null) {
        currentCoins = userData.coins;
      }

      console.log(`[COINS-DEBUG] Balance actual: ${currentCoins}, Nuevo balance después de ${amount}: ${currentCoins + amount}`);

      const newCoins = currentCoins + amount;

      // No permitir saldo negativo
      if (newCoins < 0) {
        console.error('[COINS-DEBUG] Error: Saldo insuficiente');
        return false;
      }

      // Actualizar saldo en ambas columnas
      console.log(`[COINS-DEBUG] Actualizando balance en user_coins para usuario ${userId}`);
      const { error: updateError } = await supabase
        .from('user_coins')
        .update({
          balance: newCoins,
          coins: newCoins  // Actualizar ambas columnas
        })
        .eq('user_id', userId);

      if (updateError) {
        console.error('[COINS-DEBUG] Error al actualizar coins del usuario:', updateError);
        return false;
      }

      console.log(`[COINS-DEBUG] Balance actualizado correctamente. Nuevo balance: ${newCoins}`);

      // Registrar transacción en todas las tablas disponibles
      const transactionRegistered = await registerTransactionInAllTables(
        userId,
        amount,
        'credit',
        description
      );

      if (!transactionRegistered) {
        console.warn('[COINS-DEBUG] No se pudo registrar la transacción en ninguna tabla, pero el balance fue actualizado');
      }

      console.log(`[COINS-DEBUG] Actualización de coins completada exitosamente para usuario ${userId}`);
      return true;
    });
  } catch (error) {
    console.error('[COINS-DEBUG] Error al actualizar coins del usuario después de reintentos:', error);
    return false;
  }
};

export const getUserTransactions = async (userId: string): Promise<CoinTransaction[]> => {
  try {
    console.log(`[TRANS-DEBUG] Obteniendo transacciones para usuario ${userId}`);

    return await withRetry(async () => {
      // Solo consultar la tabla coin_transactions (tabla principal)
      const { data: txData, error: txError } = await supabase
        .from('coin_transactions')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (txError) {
        console.error('[TRANS-DEBUG] Error al obtener transacciones de coin_transactions:', txError);
        return [];
      }

      if (!txData || txData.length === 0) {
        console.log('[TRANS-DEBUG] No se encontraron transacciones para el usuario');
        return [];
      }

      console.log(`[TRANS-DEBUG] Obtenidas ${txData.length} transacciones de coin_transactions`);

      // Añadir el tipo a cada transacción basado en el monto si no lo tiene
      const transactionsWithType = txData.map(tx => ({
        ...tx,
        type: tx.type || (tx.amount > 0 ? 'purchase' : tx.description.toLowerCase().includes('generación') ? 'usage' : 'debit')
      }));

      console.log('[TRANS-DEBUG] Total de transacciones:', transactionsWithType.length);

      return transactionsWithType as CoinTransaction[];
    });
  } catch (error) {
    console.error('[TRANS-DEBUG] Error al obtener transacciones después de reintentos:', error);
    return [];
  }
};

// Función para registrar una transacción de compra de monedas
export const registerPurchaseTransaction = async (
  userId: string,
  amount: number,
  description: string,
  transactionId?: string
): Promise<boolean> => {
  try {
    // Estandarizar el formato de descripción para compras
    const standardDescription = description.startsWith('Compra de')
      ? description
      : `Compra de ${amount} VCoins`;

    console.log(`[PURCHASE-DEBUG] Registrando compra para usuario ${userId}: ${amount} monedas`);

    // Usar la función estandarizada para registrar en la tabla adecuada
    const transactionRegistered = await registerTransactionInAllTables(
      userId,
      amount,
      'purchase',
      standardDescription
    );

    if (!transactionRegistered) {
      console.error('[PURCHASE-DEBUG] Error al registrar compra en tablas de transacciones');
      return false;
    }

    // Si tenemos ID de transacción, actualizamos en la tabla de transacciones
    if (transactionId) {
      try {
        const { error: updateError } = await supabase
          .from('transactions')
          .update({
            status: 'success',
            updated_at: new Date().toISOString()
          })
          .eq('transaction_id', transactionId);

        if (updateError) {
          console.error('[PURCHASE-DEBUG] Error al actualizar estado de transacción:', updateError);
        } else {
          console.log(`[PURCHASE-DEBUG] Estado de transacción ${transactionId} actualizado correctamente`);
        }
      } catch (error) {
        console.error('[PURCHASE-DEBUG] Error inesperado al actualizar estado de transacción:', error);
      }
    }

    console.log(`[PURCHASE-DEBUG] Compra registrada exitosamente para usuario ${userId}`);
    return true;
  } catch (error) {
    console.error('[PURCHASE-DEBUG] Error al registrar transacción de compra:', error);
    return false;
  }
};

// Función para verificar si el usuario tiene suficientes monedas
export const hasEnoughCoins = async (userId: string, amount: number): Promise<boolean> => {
  try {
    const userCoins = await getUserCoins(userId);

    if (!userCoins) {
      return false;
    }

    // Verificar qué columna usar para el balance
    let currentBalance = 0;
    if ('balance' in userCoins && userCoins.balance !== null) {
      currentBalance = userCoins.balance;
    } else if ('coins' in userCoins && userCoins.coins !== null) {
      currentBalance = userCoins.coins ?? 0;
    }

    return currentBalance >= amount;
  } catch (error) {
    console.error('Error al verificar saldo de coins:', error);
    return false;
  }
};

// Manejador de eventos de autenticación
supabase.auth.onAuthStateChange((event, session) => {
  if (event === 'SIGNED_IN') {
    // Reducir logs repetitivos - solo mostrar cuando sea necesario para depuración
    // console.log('Usuario conectado:', session?.user?.email);

    // Crear registro de coins si no existe
    if (session?.user?.id) {
      createUserCoinsIfNotExists(session.user.id).catch(err => {
        console.error('Error al crear coins iniciales:', err);
      });
    }
  } else if (event === 'SIGNED_OUT') {
    // console.log('Usuario desconectado');
  } else if (event === 'USER_UPDATED') {
    // console.log('Usuario actualizado');
  }
});

// Verificar conectividad
const checkConnection = async () => {
  try {
    // Usar la API de autenticación para verificar la conexión
    const { data, error } = await supabase.auth.getSession();

    if (error) throw error;
    // Eliminar log redundante de conexión
    return true;
  } catch (error) {
    console.error('Error de conexión a Supabase:', error);
    return false;
  }
};

// Verificar conexión al inicio
checkConnection().then(isConnected => {
  if (!isConnected) {
    console.warn('No se pudo establecer conexión con Supabase. La aplicación funcionará en modo offline o con datos limitados.');
  }
});

// Exportar una función para verificar la conexión bajo demanda
export const verifyConnection = checkConnection;

// Tipo para cupones
export type CouponCode = {
  id: string;
  code: string;
  coins: number;
  max_uses: number;
  current_uses: number;
  created_at: string;
  expired_at: string | null;
  is_active: boolean;
}

// Validar un cupón
export const validateCoupon = async (code: string): Promise<CouponCode | null> => {
  try {
    return await withRetry(async () => {
      const { data, error } = await supabase
        .from('coupon_codes')
        .select('*')
        .eq('code', code)
        .eq('is_active', true)
        .single();

      if (error) {
        console.error('Error al validar cupón:', error);
        return null;
      }

      const coupon = data as CouponCode;

      // Verificar si el cupón ha excedido el número máximo de usos
      if (coupon.current_uses >= coupon.max_uses) {
        return null;
      }

      // Verificar si el cupón ha expirado
      if (coupon.expired_at && new Date(coupon.expired_at) < new Date()) {
        return null;
      }

      return coupon;
    });
  } catch (error) {
    console.error('Error al validar cupón después de reintentos:', error);
    return null;
  }
};

// Sistema centralizado de control de operaciones
const operationLocks = {
  locks: new Map<string, number>(),

  // Verificar si una operación está bloqueada
  isLocked: (key: string): boolean => {
    if (typeof window === 'undefined') return false;

    // Verificar tanto en el Map como en sessionStorage para máxima compatibilidad
    const memoryLock = operationLocks.locks.has(key);
    const storageLock = window.sessionStorage.getItem(key) !== null;

    return memoryLock || storageLock;
  },

  // Bloquear una operación
  lock: (key: string, timeoutMs: number = 5000): boolean => {
    if (typeof window === 'undefined') return false;
    if (operationLocks.isLocked(key)) return false;

    // Marcar en memoria y storage
    operationLocks.locks.set(key, Date.now());
    window.sessionStorage.setItem(key, Date.now().toString());

    // Auto-desbloqueo después de timeout
    if (timeoutMs > 0) {
      setTimeout(() => operationLocks.unlock(key), timeoutMs);
    }

    return true;
  },

  // Desbloquear una operación
  unlock: (key: string): void => {
    if (typeof window === 'undefined') return;

    operationLocks.locks.delete(key);
    window.sessionStorage.removeItem(key);
  }
};

// Usar un cupón (reemplazar completamente la implementación)
export const useCoupon = async (userId: string, couponCode: string): Promise<{ success: boolean; message: string; coins?: number }> => {
  // Crear llaves únicas para el bloqueo de operaciones
  const globalLockKey = `GLOBAL_COUPON_LOCK`;
  const couponLockKey = `COUPON_OP_${userId}_${couponCode}`;
  const recentUseKey = `coupon_recent_use_${userId}_${couponCode}`;

  // Verificar si hay otra operación global de cupón en curso
  if (operationLocks.isLocked(globalLockKey)) {
    return {
      success: false,
      message: 'Hay una operación en curso, por favor espera unos segundos antes de intentar nuevamente.'
    };
  }

  // Verificar si este cupón específico está siendo procesado
  if (operationLocks.isLocked(couponLockKey)) {
    return {
      success: false,
      message: 'Este cupón ya está siendo procesado, por favor espera.'
    };
  }

  // Verificar uso reciente (almacenado en localStorage)
  if (typeof window !== 'undefined' && window.localStorage.getItem(recentUseKey)) {
    return {
      success: false,
      message: 'Este cupón ya fue utilizado recientemente. Si es válido para múltiples usos, intenta nuevamente en unos minutos.'
    };
  }

  // Establecer bloqueos
  operationLocks.lock(globalLockKey, 10000); // 10 segundos máximo
  operationLocks.lock(couponLockKey, 15000); // 15 segundos máximo

  try {
    // 1. Validar el cupón
    const coupon = await validateCoupon(couponCode);

    if (!coupon) {
      return { success: false, message: 'Cupón inválido o ya ha alcanzado el máximo de usos permitidos.' };
    }

    // 2. Verificar si el usuario ya ha utilizado este cupón antes
    try {
      const { data: usageData, error: usageError } = await supabase
        .from('user_coupon_usage')
        .select('*')
        .eq('user_id', userId)
        .eq('coupon_id', coupon.id)
        .single();

      // Si hay un registro, significa que el usuario ya usó este cupón
      if (usageData && !usageError) {
        return { success: false, message: 'Ya has utilizado este cupón anteriormente. Cada cupón se puede usar solo una vez por usuario.' };
      }
    } catch (error) {
      // Si ocurre un error (como tabla no encontrada), continuamos con el flujo
      console.log('Verificación de uso previo de cupón no disponible. Continuando con el proceso.');
    }

    // 3. Incrementar usos del cupón
    const { error: updateError } = await supabase
      .from('coupon_codes')
      .update({ current_uses: coupon.current_uses + 1 })
      .eq('id', coupon.id);

    if (updateError) {
      console.error('Error al actualizar usos del cupón:', updateError);
      return { success: false, message: 'Error al procesar el cupón. Por favor, inténtalo de nuevo.' };
    }

    // 4. Registrar que este usuario usó este cupón (solo si la tabla existe)
    try {
      const { error: usageInsertError } = await supabase
        .from('user_coupon_usage')
        .insert([{
          user_id: userId,
          coupon_id: coupon.id,
          coupon_code: couponCode,
          used_at: new Date().toISOString(),
          coins_added: coupon.coins
        }]);

      if (usageInsertError) {
        console.error('Error al registrar uso del cupón:', usageInsertError);
        // No abortamos el proceso si falla este registro, pero lo logueamos
      }
    } catch (error) {
      console.error('Error al registrar uso del cupón (posiblemente la tabla no existe):', error);
      // Continuamos con el flujo principal
    }

    // 5. Obtener balance actual
    const userCoinsData = await getUserCoins(userId);
    if (!userCoinsData) {
      return { success: false, message: 'Error al obtener información de usuario. Por favor, inténtalo de nuevo.' };
    }

    // 6. Calcular nuevo balance
    const currentBalance = userCoinsData.balance || 0;
    const newBalance = currentBalance + coupon.coins;

    // Registro simplificado
    console.log(`Actualizando balance de ${currentBalance} a ${newBalance}`);

    // 7. Actualizar balance en un solo paso
    const { error: balanceUpdateError } = await supabase
      .from('user_coins')
      .update({
        balance: newBalance,
        coins: newBalance,
        updated_at: new Date().toISOString()
      })
      .eq('user_id', userId);

    if (balanceUpdateError) {
      console.error('Error al actualizar balance:', balanceUpdateError);
      return { success: false, message: 'Error al actualizar tu balance de monedas. Por favor, inténtalo de nuevo.' };
    }

    // 8. Registrar la transacción
    const transactionId = `coupon_${couponCode}_${Date.now()}_${Math.random().toString(36).substring(2, 10)}`;

    const { error: transactionError } = await supabase
      .from('coin_transactions')
      .insert([{
        user_id: userId,
        amount: coupon.coins,
        type: 'credit',
        description: `Monedas añadidas por cupón ${couponCode}`,
        status: 'completed',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        transaction_id: transactionId // ID único para evitar duplicados
      }]);

    if (transactionError) {
      console.error('Error al registrar transacción:', transactionError);
      return { success: false, message: 'Error al registrar la transacción. Por favor, inténtalo de nuevo.' };
    }

    // 9. Marcar como recientemente usado (5 minutos)
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(recentUseKey, 'true');
      setTimeout(() => {
        window.localStorage.removeItem(recentUseKey);
      }, 5 * 60 * 1000);
    }

    // 10. Añadir una pausa para asegurar que todas las operaciones se completen
    await new Promise(resolve => setTimeout(resolve, 500));

    // 11. Disparar evento de actualización (solo una vez)
    if (typeof window !== 'undefined') {
      console.log('Disparando evento coins-updated una sola vez');
      window.dispatchEvent(new CustomEvent('coins-updated'));
    }

    return {
      success: true,
      message: `¡${coupon.coins} monedas añadidas a tu cuenta! Tu nuevo saldo es de ${newBalance} monedas.`,
      coins: coupon.coins
    };
  } catch (error) {
    console.error('Error al usar cupón:', error);
    return { success: false, message: 'Error al procesar el cupón. Por favor, inténtalo de nuevo más tarde.' };
  } finally {
    // Asegurar que los bloqueos se liberen después de un tiempo
    setTimeout(() => {
      operationLocks.unlock(couponLockKey);
      operationLocks.unlock(globalLockKey);
    }, 3000);
  }
};